import React, { useCallback, useEffect, useRef, useState } from 'react';
import { registerConfirmAlert } from '@/lib/confirmAlert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DEFAULTS = {
  title: 'Confirm',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirmClass: 'btn-primary',
};

/**
 * App-root host for promise-based confirmAlert().
 */
export default function ConfirmAlertHost() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolveRef = useRef(null);

  const finish = useCallback((value) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setOpen(false);
    resolve?.(value);
  }, []);

  useEffect(() => {
    registerConfirmAlert((options) => {
      if (resolveRef.current) {
        // Already showing — reject stacking; resolve previous as cancel
        resolveRef.current(false);
        resolveRef.current = null;
      }
      return new Promise((resolve) => {
        resolveRef.current = resolve;
        setOpts({ ...DEFAULTS, ...options });
        setOpen(true);
      });
    });
    return () => registerConfirmAlert(null);
  }, []);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) finish(false);
      }}
    >
      <AlertDialogContent unstyled>
        <AlertDialogHeader className="contents">
          <AlertDialogTitle unstyled id="confirmDialogTitle">
            {opts.title}
          </AlertDialogTitle>
          {opts.message ? (
            <AlertDialogDescription unstyled>{opts.message}</AlertDialogDescription>
          ) : (
            <AlertDialogDescription unstyled className="sr-only">
              {opts.title}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter className="confirm-dialog-actions">
          <AlertDialogCancel
            className="btn btn-secondary"
            onClick={() => finish(false)}
          >
            {opts.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            className={`btn ${opts.confirmClass}`}
            onClick={() => finish(true)}
          >
            {opts.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
